import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Use this hook to detect if the nearest scrollable parent of an element has been scrolled.  Used for visual effects
 * @returns A ref to the element and a boolean indicating if the nearest scrollable parent has been scrolled.
 * @example
 * ```tsx
 * const { ref, hasScrolled } = useHasScrolled();
 * return (
 *   <div ref={ref}>
 *     {hasScrolled && <div>Scrolled</div>}
 *   </div>
 * );
 * ```
 */
export function useHasScrolled() {
  const [hasScrolled, setHasScrolled] = useState(false);
  const cleanupRef = useRef<() => void>();

  const handleScroll = useCallback((e: Event) => {
    const target = e.currentTarget;
    if (!(target instanceof HTMLElement)) return;
    setHasScrolled(target.scrollTop > 0);
  }, []);

  const ref = useCallback(
    (node: HTMLElement | null) => {
      cleanupRef.current?.();
      cleanupRef.current = undefined;
      if (!node) return;
      function getScrollParent(node: HTMLElement | null) {
        if (!node) return null;
        const style = getComputedStyle(node);

        if (
          node.scrollHeight > node.clientHeight ||
          style.overflowY === "scroll" ||
          style.overflowY === "auto" ||
          node.dataset["radixScrollAreaViewport"] != null
        ) {
          return node;
        }
        return getScrollParent(node.parentElement);
      }
      const scrollParent = getScrollParent(node?.parentElement);
      if (scrollParent) {
        scrollParent.addEventListener("scroll", handleScroll);
      }
      cleanupRef.current = () => {
        if (scrollParent) {
          scrollParent.removeEventListener("scroll", handleScroll);
        }
      };
    },
    [handleScroll],
  );

  return { ref, hasScrolled };
}
